import type { EnvelopeState, RecipientState } from '@signbento/contracts'
import type { MessageKey } from '@signbento/ui'

/**
 * One vocabulary for where an agreement stands, used by every screen that shows a status: Home,
 * Agreements, the post-send page and Request detail. Before this, the same request read "Sent",
 * "Active", "ACTIVE" and "Awaiting response" on four screens.
 */
export type StatusTone = 'neutral' | 'info' | 'warning' | 'success' | 'danger'

export interface StatusWord {
  readonly key: MessageKey
  readonly tone: StatusTone
}

const REQUEST_STATUS: Readonly<Record<EnvelopeState, StatusWord>> = {
  LOCAL_DRAFT: { key: 'status.draft', tone: 'neutral' },
  PREPARING: { key: 'status.sending', tone: 'info' },
  PREPARATION_FAILED: { key: 'status.send_failed', tone: 'danger' },
  ACTIVE: { key: 'status.waiting', tone: 'info' },
  // Everyone signed; the sender's device still has to finish the final PDF.
  AWAITING_FINALIZATION: { key: 'status.ready_to_finish', tone: 'warning' },
  COMPLETED: { key: 'status.completed', tone: 'success' },
  FINALIZATION_FAILED: { key: 'status.finish_failed', tone: 'danger' },
  DECLINED: { key: 'status.declined', tone: 'danger' },
  VOIDED: { key: 'status.voided', tone: 'neutral' },
  EXPIRED: { key: 'status.expired', tone: 'neutral' },
}

/** The status word for a request's server state. */
export function requestStatusWord(state: EnvelopeState): StatusWord {
  return REQUEST_STATUS[state]
}

/** An active request whose next step is the sender's own signature, as its hero says. */
export const SENDER_TURN_WORD: StatusWord = { key: 'status.your_turn', tone: 'warning' }

const PARTICIPANT_STATUS: Readonly<Record<RecipientState, StatusWord>> = {
  BLOCKED: { key: 'status.participant_not_yet', tone: 'neutral' },
  ACTIVE: { key: 'status.participant_waiting', tone: 'info' },
  SUBMITTED: { key: 'status.participant_signed', tone: 'success' },
  DECLINED: { key: 'status.participant_declined', tone: 'danger' },
}

/** Requests that can no longer collect a signature. */
const CLOSED: ReadonlySet<EnvelopeState> = new Set([
  'VOIDED',
  'EXPIRED',
  'DECLINED',
  'FINALIZATION_FAILED',
  'PREPARATION_FAILED',
])

/**
 * The status word for one participant's step. CC rows read "Gets a copy" instead. Once the request
 * is closed, nobody is still "waiting to sign": a step that never signed reads "Not signed".
 */
export function participantStatusWord(
  state: RecipientState,
  kind: 'signer' | 'cc',
  request?: EnvelopeState,
): StatusWord {
  if (kind === 'cc') return { key: 'status.participant_cc', tone: 'neutral' }
  if (request !== undefined && CLOSED.has(request) && (state === 'ACTIVE' || state === 'BLOCKED')) {
    return { key: 'status.participant_not_signed', tone: 'neutral' }
  }
  return PARTICIPANT_STATUS[state]
}
